import React, { useState, useEffect } from 'react';
import { companyAPI, adminAPI } from '../services/api';
import logger from '../utils/logger';
import { PageLayout, DataTable, Badge, SearchFilterBar, ConfirmDialog, StatusBadge } from './ui';
import { Building2, Users, Shield, Server, Lock, AlertTriangle, Save, X, Trash2 } from 'lucide-react';

const CompanyManager = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  
  const [panelCompany, setPanelCompany] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showActiveOnly]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (showActiveOnly) params.active_only = true;
      if (searchTerm) params.search = searchTerm;
      
      const data = await companyAPI.listCompanies(params);
      setCompanies(data);
    } catch (err) {
      logger.error('Failed to load companies:', err);
      setError(err.response?.data?.error || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const openCompanyPanel = (company) => {
    setPanelCompany(company);
  };

  const closePanel = () => {
    setPanelCompany(null);
  };

  const handleDeleteCompany = async (company) => {
    try {
      await companyAPI.deleteCompany(company.id);
      setSuccess(`Company "${company.name}" deleted successfully`);
      setPanelCompany(null);
      
      // Reload page to refresh navbar company list
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      logger.error('Failed to delete company:', err);
      setError(err.response?.data?.error || 'Failed to delete company');
      setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });
    }
  };

  const openCreatePanel = () => {
    setPanelCompany({ id: null, name: '', is_active: true, is_internal: false, mfa_policy: 'none', mfa_sso_step_up: false });
  };

  const handleCompanyUpdated = async () => {
    await loadCompanies();
    if (panelCompany && panelCompany.id) {
      const updated = companies.find(c => c.id === panelCompany.id);
      if (updated) setPanelCompany(updated);
    }
  };

  return (
    <PageLayout
      title="Company Management"
      backTo="/management"
      backLabel="Back to Management"
      actions={
        <button onClick={openCreatePanel} className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-hover text-sm font-medium">
          + Add Company
        </button>
      }
    >
      {success && (
        <div className="bg-green-50 border-l-4 border-green-400 p-3 mb-4 text-sm text-green-700">{success}</div>
      )}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-3 mb-4 text-sm text-red-700">{error}</div>
      )}

      <SearchFilterBar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search companies by name or domain..."
        filters={[
          {
            value: showActiveOnly ? 'active' : '',
            onChange: (v) => setShowActiveOnly(v === 'active'),
            options: [
              { value: '', label: 'All Companies' },
              { value: 'active', label: 'Active Only' },
            ],
          },
        ]}
      />

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading companies...</div>
      ) : (
        <DataTable
          columns={[
            {
              key: 'name',
              label: 'Name',
              sortable: true,
              render: (company) => (
                <div>
                  <div className="flex items-center gap-2">
                    {company.logo_url && <img src={company.logo_url} alt="" className="w-6 h-6 rounded" />}
                    <strong className="text-gray-900">{company.name}</strong>
                  </div>
                  {company.description && <div className="text-xs text-gray-500 mt-1">{company.description}</div>}
                </div>
              ),
            },
            {
              key: 'domain',
              label: 'Domain',
              sortable: true,
              render: (company) => <span className="text-sm text-gray-600">{company.domain || '—'}</span>,
            },
            {
              key: 'user_count',
              label: 'Users',
              sortable: true,
              render: (company) => <span className="text-sm text-gray-600">{company.user_count} users</span>,
            },
            {
              key: 'contact_email',
              label: 'Contact',
              render: (company) => <span className="text-sm text-gray-600">{company.contact_email || '—'}</span>,
            },
            {
              key: 'is_active',
              label: 'Status',
              sortable: true,
              render: (company) => <StatusBadge status={company.is_active ? 'active' : 'inactive'} />,
            },
          ]}
          data={companies}
          onRowClick={openCompanyPanel}
          emptyMessage="No companies found"
        />
      )}

      {panelCompany && (
        <CompanyDetailPanel
          key={panelCompany.id || 'new'}
          company={panelCompany}
          onClose={closePanel}
          onUpdated={handleCompanyUpdated}
          onDelete={(company) => setConfirmDialog({
            open: true,
            title: 'Delete Company',
            message: `Are you sure you want to delete "${company.name}"? This will unassign ${company.user_count || 0} user(s).`,
            onConfirm: () => handleDeleteCompany(company),
            variant: 'danger'
          })}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Delete"
        variant={confirmDialog.variant}
      />
    </PageLayout>
  );
};

// --- Company Detail Panel Component ---
const CompanyDetailPanel = ({ company, onClose, onUpdated, onDelete }) => {
  const isNew = !company.id;
  const [tab, setTab] = useState('General');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    name: company.name || '',
    description: company.description || '',
    domain: company.domain || '',
    logo_url: company.logo_url || '',
    contact_email: company.contact_email || '',
    contact_phone: company.contact_phone || '',
    address: company.address || '',
    is_active: company.is_active ?? true,
    is_internal: company.is_internal || false,
    mfa_policy: company.mfa_policy || 'none',
    mfa_sso_step_up: company.mfa_sso_step_up || false
  });
  
  const [companyUsers, setCompanyUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  
  const [allRoles, setAllRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  
  const [bcServiceTypes, setBcServiceTypes] = useState([]);
  
  const [serviceInstances, setServiceInstances] = useState([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  
  const [lockoutMaxAttempts, setLockoutMaxAttempts] = useState(company.lockout_max_attempts || 5);
  const [lockoutDurations, setLockoutDurations] = useState(company.lockout_durations || '15,30,60');
  const [maxConcurrentSessions, setMaxConcurrentSessions] = useState(company.max_concurrent_sessions != null ? String(company.max_concurrent_sessions) : '');

  const tabs = isNew 
    ? ['General']
    : ['General', 'Access', 'Security', 'Advanced'];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveGeneral = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    // Validate logo_url if provided
    if (formData.logo_url && formData.logo_url.trim()) {
      try {
        const url = new URL(formData.logo_url);
        if (!['http:', 'https:'].includes(url.protocol)) {
          setError('Logo URL must start with http:// or https://');
          setLoading(false);
          return;
        }
      } catch {
        setError('Logo URL must be a valid URL (e.g., https://example.com/logo.png)');
        setLoading(false);
        return;
      }
    }
    
    try {
      if (isNew) {
        await companyAPI.createCompany(formData);
        setSuccess('Company created successfully');
        setTimeout(() => onClose(), 1500);
      } else {
        await companyAPI.updateCompany(company.id, formData);
        setSuccess('Company updated successfully');
      }
      await onUpdated();
    } catch (err) {
      logger.error('Failed to save company:', err);
      setError(err.response?.data?.error || 'Failed to save company');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!company.id || isNew) return;
    
    if (tab === 'Access') {
      if (companyUsers.length === 0) loadCompanyUsers();
      if (allRoles.length === 0) loadAllowedRoles();
      if (serviceInstances.length === 0) loadServiceInstances();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, company.id]);

  const loadCompanyUsers = async () => {
    try {
      setUsersLoading(true);
      const users = await companyAPI.getCompanyUsers(company.id);
      setCompanyUsers(users);
    } catch (err) {
      logger.error('Failed to load company users:', err);
      setError(err.response?.data?.error || 'Failed to load company users');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadAllowedRoles = async () => {
    try {
      setRolesLoading(true);
      const data = await companyAPI.getAllowedRoles(company.id);
      setAllRoles(data.all_roles || []);
      setBcServiceTypes(data.bc_service_types || []);
      const allowedIds = (data.allowed_roles || []).map(r => r.role_id);
      setSelectedRoleIds(allowedIds);
    } catch (err) {
      logger.error('Failed to load allowed roles:', err);
      setError(err.response?.data?.error || 'Failed to load allowed roles');
    } finally {
      setRolesLoading(false);
    }
  };

  const handleSaveAllowedRoles = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await companyAPI.setAllowedRoles(company.id, selectedRoleIds);
      setSuccess('Allowed roles updated');
    } catch (err) {
      logger.error('Failed to save allowed roles:', err);
      setError(err.response?.data?.error || 'Failed to save allowed roles');
    } finally {
      setLoading(false);
    }
  };

  const toggleRoleSelection = (roleId) => {
    setSelectedRoleIds(prev =>
      prev.includes(roleId) ? prev.filter(id => id !== roleId) : [...prev, roleId]
    );
  };

  // Service types are pulled automatically from BC — no manual toggle needed

  const loadServiceInstances = async () => {
    try {
      setInstancesLoading(true);
      const data = await companyAPI.listServiceInstances(company.id);
      setServiceInstances(data.service_instances || []);
    } catch (err) {
      logger.error('Failed to load service instances:', err);
      setError(err.response?.data?.error || 'Failed to load service instances');
    } finally {
      setInstancesLoading(false);
    }
  };

  const handleToggleInstanceActive = async (instance) => {
    try {
      const data = await companyAPI.updateServiceInstance(company.id, instance.id, { is_active: !instance.is_active });
      setServiceInstances(serviceInstances.map(si => si.id === instance.id ? data.service_instance : si));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update service instance');
    }
  };

  const handleSavePolicies = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const durations = lockoutDurations.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d) && d > 0);
      await adminAPI.setLockoutPolicy(company.id, {
        lockout_max_attempts: lockoutMaxAttempts,
        lockout_durations: durations,
      });
      
      const maxSessions = maxConcurrentSessions ? parseInt(maxConcurrentSessions) : null;
      await adminAPI.setSessionPolicy(company.id, maxSessions);
      
      setSuccess('Security policies updated');
      await onUpdated();
    } catch (err) {
      logger.error('Failed to save policies:', err);
      setError(err.response?.data?.error || 'Failed to save policies');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

  return (
    <div className="fixed right-0 bottom-0 bg-white border-l border-gray-200 shadow-2xl z-40 overflow-y-auto" 
         style={{ top: '64px', width: '500px' }}>
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center gap-3 z-10">
        <Building2 className="w-6 h-6 text-primary" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 truncate">{company.name || 'New Company'}</div>
          {company.domain && <div className="text-sm text-gray-500 truncate">{company.domain}</div>}
        </div>
        {!isNew && <StatusBadge status={company.is_active ? 'active' : 'inactive'} />}
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-2">×</button>
      </div>

      {/* Tabs */}
      {!isNew && (
        <div className="sticky top-[73px] bg-white border-b border-gray-200 flex overflow-x-auto z-10">
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === t ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {t}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="px-6 pt-7 pb-10">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-3 text-sm text-red-700 mb-4">{error}</div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-400 p-3 text-sm text-green-700 mb-4">{success}</div>
        )}

        {/* General tab */}
        {tab === 'General' && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Company Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Acme Corporation"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                placeholder="Brief description of the company"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Domain</label>
              <input
                type="text"
                name="domain"
                value={formData.domain}
                onChange={handleInputChange}
                placeholder="acme.com"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Logo URL</label>
              <input
                type="url"
                name="logo_url"
                value={formData.logo_url}
                onChange={handleInputChange}
                placeholder="https://example.com/logo.png"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Contact Email</label>
              <input
                type="email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleInputChange}
                placeholder="contact@acme.com"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Contact Phone</label>
              <input
                type="tel"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleInputChange}
                placeholder="+1 234 567 8900"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="2"
                placeholder="Company address"
                className={inputClass}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <label className="text-sm text-gray-700">Active</label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="is_internal"
                checked={formData.is_internal}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <label className="text-sm text-gray-700">Internal company (all roles allowed)</label>
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">MFA Policy</label>
              <select
                name="mfa_policy"
                value={formData.mfa_policy}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary focus:border-primary"
              >
                <option value="none">None</option>
                <option value="any">Require MFA (TOTP or Security Key)</option>
                <option value="webauthn_only">Require Security Key (WebAuthn only)</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="mfa_sso_step_up"
                checked={formData.mfa_sso_step_up}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <label className="text-sm text-gray-700">Require MFA even after SSO login</label>
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={handleSaveGeneral}
                disabled={loading || !formData.name.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 text-sm font-medium"
              >
                <Save className="w-4 h-4" />
                {isNew ? 'Create Company' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* Access tab - combines Users, Roles, Service Types, Service Instances */}
        {tab === 'Access' && !isNew && (
          <div className="space-y-6">
            {/* Users section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Users
              </h3>
              <div className="pl-6 space-y-3">
                <p className="text-sm text-gray-500">Users assigned to this company. Manage assignments from User Management.</p>
                {usersLoading ? (
                  <p className="text-gray-500 text-sm text-center py-6">Loading users...</p>
                ) : companyUsers.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-6 bg-gray-50 border border-gray-200 rounded-lg">No users in this company</p>
                ) : (
                  <div className="space-y-2">
                    {companyUsers.map(user => (
                      <div key={user.id} className="p-3 border border-gray-200 rounded-lg">
                        <div className="font-medium text-sm text-gray-900">{user.firstname} {user.lastname}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                        <div className="text-xs text-gray-400 mt-1">Permission: {user.permission_level}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Roles section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Allowed Roles
              </h3>
              <div className="pl-6 space-y-3">
                <div className="text-sm text-gray-500 space-y-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="font-medium text-blue-900">Select which roles can be assigned to users in this company.</p>
                  <p className="text-blue-700">Users can only be assigned roles from this list.</p>
                </div>

                {selectedRoleIds.length > 0 && (
                  <p className="text-sm text-amber-700 font-medium bg-amber-50 border border-amber-200 rounded-lg p-3">
                    Role restriction active — only the {selectedRoleIds.length} checked role(s) can be assigned to users in this company.
                  </p>
                )}
                {selectedRoleIds.length === 0 && (
                  <p className="text-sm text-red-700 font-medium bg-red-50 border border-red-200 rounded-lg p-3">
                    No roles are authorized for this company yet. Select one or more roles from the list below to enable user assignments.
                  </p>
                )}

                {rolesLoading ? (
                  <p className="text-gray-500 text-sm">Loading roles...</p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {allRoles.map(role => (
                      <label key={role.id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${selectedRoleIds.includes(role.id) ? 'border-primary bg-indigo-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                        <input
                          type="checkbox"
                          checked={selectedRoleIds.includes(role.id)}
                          onChange={() => toggleRoleSelection(role.id)}
                          className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                        />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{role.name}</div>
                          {role.description && (
                            <div className="text-xs text-gray-500">{role.description}</div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <button
                    onClick={handleSaveAllowedRoles}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 text-sm font-medium"
                  >
                    <Save className="w-4 h-4" />
                    Save Roles
                  </button>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Service Types section — read-only, pulled from BC */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Server className="w-4 h-4" />
                Service Types
              </h3>
              <div className="pl-6 space-y-3">
                <div className="text-sm text-gray-500 space-y-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="font-medium text-blue-900">Service types are pulled automatically from Business Commander.</p>
                  <p className="text-blue-700">Roles are filtered to only show those matching these service types.</p>
                </div>

                {bcServiceTypes.length === 0 ? (
                  <p className="text-sm text-amber-700 font-medium bg-amber-50 border border-amber-200 rounded-lg p-2">
                    No service types found in BC for &ldquo;{company.name}&rdquo;. No roles will be available until this company exists in Business Commander.
                  </p>
                ) : (
                  <div className="space-y-1">
                    {bcServiceTypes.map(st => (
                      <span key={st} className="inline-block mr-2 mb-2 px-3 py-1 bg-indigo-50 border border-indigo-200 rounded-full text-sm font-medium text-indigo-700">
                        {st}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200"></div>

            {/* Service Instances section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Server className="w-4 h-4" />
                Service Instances
              </h3>
              <div className="pl-6 space-y-4">
                <div className="text-sm bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                  <p className="font-medium text-blue-900">Service instances are automatically synced from BackupCare.</p>
                  <p className="text-blue-700">Each instance represents a separate BC service account (e.g. "Veeam Production", "Veeam DR"). Roles can be scoped to specific instances for per-instance permissions.</p>
                  <p className="text-blue-700">You can activate or deactivate instances to control which ones are available for role assignment.</p>
                </div>

                {/* Instance list */}
                {instancesLoading ? (
                  <p className="text-gray-500 text-sm text-center py-6">Loading instances...</p>
                ) : serviceInstances.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-6 bg-gray-50 border border-gray-200 rounded-lg">
                    No service instances synced yet. Instances will appear here when BackupCare syncs service accounts for this company.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {serviceInstances.map(si => (
                      <div key={si.id} className={`flex items-center justify-between p-4 border rounded-lg ${si.is_active ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50'}`}>
                        <div>
                          <div className={`font-semibold text-sm ${si.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
                            {si.instance_name}
                            <span className="ml-2 text-xs text-gray-500 font-normal">{si.service_type}</span>
                            {!si.is_active && (
                              <span className="ml-2 text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">inactive</span>
                            )}
                          </div>
                          {si.bc_service_account_id && (
                            <div className="text-xs text-gray-400 mt-1">BC Service Account: {si.bc_service_account_id}</div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleToggleInstanceActive(si)}
                            className={`text-sm px-3 py-1.5 rounded border transition-colors ${si.is_active ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-green-600 text-green-600 hover:bg-green-50'}`}
                          >
                            {si.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Security tab */}
        {tab === 'Security' && !isNew && (
          <div className="space-y-4">
            <div className="text-sm text-gray-500 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="font-medium text-blue-900">Configure security policies for this company.</p>
              <p className="text-blue-700 mt-1">These policies apply to all users in this company.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Failed Login Attempts</label>
              <input
                type="number"
                min="1"
                max="20"
                value={lockoutMaxAttempts}
                onChange={(e) => setLockoutMaxAttempts(parseInt(e.target.value) || 5)}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500">Account locks after this many consecutive failed logins.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lockout Durations (minutes, comma-separated)</label>
              <input
                type="text"
                value={lockoutDurations}
                onChange={(e) => setLockoutDurations(e.target.value)}
                placeholder="15,30,60"
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500">Escalating lockout durations for repeated lockouts. e.g. 15,30,60 means 1st lockout=15min, 2nd=30min, 3rd+=60min.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Concurrent Sessions</label>
              <input
                type="number"
                min="1"
                value={maxConcurrentSessions}
                onChange={(e) => setMaxConcurrentSessions(e.target.value)}
                placeholder="No limit"
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500">Leave empty for no limit. When exceeded, oldest session is revoked.</p>
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={handleSavePolicies}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 text-sm font-medium"
              >
                <Save className="w-4 h-4" />
                Save Policies
              </button>
            </div>
          </div>
        )}

        {/* Advanced tab */}
        {tab === 'Advanced' && !isNew && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900 text-sm">Danger Zone</h3>
                  <p className="text-sm text-red-700 mt-1">Actions in this section are irreversible and may cause data loss.</p>
                </div>
              </div>
            </div>

            {company.id === 1 ? (
              <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 text-sm text-amber-800">
                <strong>System Operator Tenant:</strong> This is the root/primary system operator company and cannot be deleted to protect platform system integrity.
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">Delete Company</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Permanently delete this company and unassign {company.user_count || 0} user(s).
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      This action cannot be undone. User accounts will not be deleted, only unassigned from this company.
                    </p>
                  </div>
                  <button
                    onClick={() => onDelete(company)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyManager;
